/**
 * Enlaces al catálogo de landings (clásicas + genéricas).
 *
 * Fuente única para la nav (desktop vs mobile) y el footer: así no crece el
 * menú de escritorio cuando se añaden ocasiones, y el footer / drawer móvil
 * sí las listan todas.
 */

import { toPublicLocale, type PublicLocale } from '@/lib/locale-prefix'
import { getBrand, isCatalogEnabled, isFamilyEnabled, isTrackerEnabled, type BrandFamily } from '@/lib/brands'
import { getCanonicalFamilyLandingHref, getFamilyLandingHref, getFreeSantaHref } from '@/lib/locale-paths'
import {
  LANDING_LOCALES,
  getConejoPascuaHref,
  getCumpleanosHref,
  getHalloweenHref,
  getMundoMagicoHref,
} from '@/lib/generic-landings/paths'
import { CUSTOM_LANDING_PUBLISHED, surfaceHref } from '@/lib/locale-capabilities'
import { isCustomCharacterLandingEnabled } from '@/lib/feature-flags'
// Import directo al submódulo de rutas (no al barril): este fichero llega al
// bundle cliente vía LandingNav y el barril arrastraría todo el contenido del
// tracker (copy de todos los idiomas publicados, itinerario, mapa).
import { TRACKER_LOCALES, getSantaTrackerHref } from '@/lib/santa-tracker/paths'

/** Claves de `landing.navigation.*` usadas como etiqueta del enlace. */
export const LANDING_CATALOG_LABEL_KEYS = [
  'santa',
  'reyes',
  'hada',
  'raton',
  'conejo',
  'halloween',
  'cumpleanos',
  'mundoMagico',
  'tracker',
  'tuPersonaje',
  'freeSanta',
] as const

export type LandingCatalogLabelKey = (typeof LANDING_CATALOG_LABEL_KEYS)[number]

export type LandingCatalogLink = {
  id: LandingCatalogLabelKey
  href: string
  labelKey: LandingCatalogLabelKey
}

type ClassicNavId = 'santa' | 'reyes' | 'hada' | 'raton'

/** Asociación de etiquetas de nav a familias; las rutas las resuelve una sola función. */
const CLASSIC_FAMILY_BY_NAV_ID = {
  santa: 'santa',
  reyes: 'reyes',
  hada: 'tooth-fairy',
  raton: 'raton-perez',
} as const satisfies Record<ClassicNavId, BrandFamily>

type NonClassicNavId = Exclude<LandingCatalogLabelKey, ClassicNavId>

/**
 * Entradas que no son familias clásicas. Las rutas del catálogo genérico y del
 * tracker conservan sus propios registros de publicación.
 */
const GENERIC_NAV_LINKS_BY_ID: Record<
  NonClassicNavId,
  { href: (locale: PublicLocale) => string | undefined; published: readonly string[] | null; enabled: () => boolean }
> = {
  conejo: { href: getConejoPascuaHref, published: LANDING_LOCALES, enabled: isCatalogEnabled },
  halloween: { href: getHalloweenHref, published: LANDING_LOCALES, enabled: isCatalogEnabled },
  cumpleanos: { href: getCumpleanosHref, published: LANDING_LOCALES, enabled: isCatalogEnabled },
  mundoMagico: { href: getMundoMagicoHref, published: LANDING_LOCALES, enabled: isCatalogEnabled },
  tracker: { href: getSantaTrackerHref, published: TRACKER_LOCALES, enabled: isTrackerEnabled },
  // La landing del personaje propio NO va en la nav (va justa a 1024px): solo
  // footer. Sale solo con su flag de despliegue encendida — landing, sitemap y
  // enlace entran y salen juntos (ver lib/feature-flags).
  tuPersonaje: {
    href: (l) => surfaceHref('custom-landing', l),
    published: CUSTOM_LANDING_PUBLISHED,
    enabled: isCustomCharacterLandingEnabled,
  },
  // La landing «Papá Noel gratis»: solo footer (enlazado interno de todo el sitio
  // con su ancla de búsqueda), publicada en los 7 idiomas mientras exista Santa.
  freeSanta: { href: getFreeSantaHref, published: null, enabled: () => isFamilyEnabled('santa') },
}

function isClassicNavId(id: LandingCatalogLabelKey): id is ClassicNavId {
  return id in CLASSIC_FAMILY_BY_NAV_ID
}

/** Clásicas + hub: caben en la nav de escritorio a `lg`. */
export const DESKTOP_NAV_CHARACTER_IDS: readonly LandingCatalogLabelKey[] = [
  'santa',
  'reyes',
  'hada',
  'raton',
  'mundoMagico',
]

/**
 * Ocasiones del catálogo genérico y el tracker: solo en menú móvil y footer.
 * No entran en desktop (medido: en inglés las clásicas ya llenan los 1024px).
 */
export const MOBILE_ONLY_CHARACTER_IDS: readonly LandingCatalogLabelKey[] = [
  'conejo',
  'halloween',
  'cumpleanos',
  'tracker',
]

/** Orden del drawer móvil: clásicas → ocasiones → hub → tracker. */
export const MOBILE_NAV_CHARACTER_IDS: readonly LandingCatalogLabelKey[] = [
  'santa',
  'reyes',
  'hada',
  'raton',
  'conejo',
  'halloween',
  'cumpleanos',
  'mundoMagico',
  'tracker',
]

/** Catálogo completo del footer: el móvil, la landing del personaje propio y la de «Papá Noel gratis». */
export const FOOTER_CATALOG_IDS: readonly LandingCatalogLabelKey[] = [
  ...MOBILE_NAV_CHARACTER_IDS,
  'tuPersonaje',
  'freeSanta',
]

/** El footer de Ratón enlaza el catálogo de PapaNoel.chat, sin repetir el Ratón ni el tracker. */
const RATON_FOOTER_CATALOG_IDS = [
  'santa',
  'reyes',
  'hada',
  'conejo',
  'halloween',
  'cumpleanos',
  'mundoMagico',
  'tuPersonaje',
] as const satisfies readonly LandingCatalogLabelKey[]

function linksFor(
  ids: readonly LandingCatalogLabelKey[],
  locale: string
): LandingCatalogLink[] {
  const pl = toPublicLocale(locale)
  return ids.flatMap((id) => {
    if (isClassicNavId(id)) {
      const family = CLASSIC_FAMILY_BY_NAV_ID[id]
      if (!isFamilyEnabled(family)) return []
      const link: LandingCatalogLink = { id, href: getFamilyLandingHref(family, pl), labelKey: id }
      return [link]
    }

    const { published, enabled, href } = GENERIC_NAV_LINKS_BY_ID[id]
    if (!enabled() || (published && !published.includes(pl))) return []
    const resolvedHref = href(pl)
    return resolvedHref === undefined ? [] : [{ id, href: resolvedHref, labelKey: id }]
  })
}

export function getDesktopNavCharacterLinks(locale: string): LandingCatalogLink[] {
  return linksFor(DESKTOP_NAV_CHARACTER_IDS, locale)
}

export function getMobileNavCharacterLinks(locale: string): LandingCatalogLink[] {
  return linksFor(MOBILE_NAV_CHARACTER_IDS, locale)
}

export function getFooterCatalogLinks(locale: string): LandingCatalogLink[] {
  if (getBrand().id === 'raton-perez') return ratonFooterLinks(locale)
  return linksFor(FOOTER_CATALOG_IDS, locale)
}

function ratonFooterLinks(locale: string): LandingCatalogLink[] {
  const pl = toPublicLocale(locale)
  const papaNoelOrigin = new URL(getCanonicalFamilyLandingHref('santa', pl)).origin

  return RATON_FOOTER_CATALOG_IDS.flatMap<LandingCatalogLink>((id) => {
    if (isClassicNavId(id)) {
      return [{
        id,
        href: getCanonicalFamilyLandingHref(CLASSIC_FAMILY_BY_NAV_ID[id], pl),
        labelKey: id,
      }]
    }

    const { published, enabled, href } = GENERIC_NAV_LINKS_BY_ID[id]
    if (published && !published.includes(pl)) return []
    // Tu Personaje solo se enlaza cuando la landing está habilitada, igual que
    // en el footer del sitio principal; así no aparece un enlace a una 404.
    if (id === 'tuPersonaje' && !enabled()) return []

    const path = href(pl)
    return path ? [{ id, href: new URL(path, papaNoelOrigin).toString(), labelKey: id }] : []
  })
}
